import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useUser } from '@/hooks/useUser';
import { useSocialAccounts } from '@/hooks/useSocialAccounts';
import Tabs from '@/components/common/Tabs';
import ProfileSettings from '@/components/profile/ProfileSettings';
import { NotificationSettings } from '@/components/auth/NotificationSettings';
import PostSettings from '@/components/post/PostSettings';
import TierSettings from '@/components/tier/TierSettings';

const ProfilePage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const tab = searchParams.get('tab');
  const code = searchParams.get('code');
  const state = searchParams.get('state');
  const platform = searchParams.get('platform');
  const { refreshUserData } = useUser();
  const { refreshAccounts } = useSocialAccounts();
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  
  // Handle OAuth callbacks - only run this once when the page loads with OAuth params
  useEffect(() => {
    // Only process OAuth callback if we have code and state params
    if (code && state && platform && !initialLoadComplete) {
      console.log(`Processing OAuth callback for ${platform}`);
      
      // After processing the OAuth callback, refresh user data and social accounts
      const processOAuthCallback = async () => {
        try {
          // Refresh user data and social accounts
          await Promise.all([refreshUserData(), refreshAccounts()]);
          
          // Clear the query params to prevent reprocessing on navigation
          navigate('/profile', { replace: true });
        } catch (error) {
          console.error('Error processing OAuth callback:', error);
        }
      };
      
      processOAuthCallback();
    }
  }, [code, state, platform, navigate, refreshUserData, refreshAccounts, initialLoadComplete]);
  
  // Handle initial data load - only run once on mount
  useEffect(() => {
    if (!initialLoadComplete) {
      console.log('ProfilePage - INITIAL LOAD');
      
      const loadInitialData = async () => {
        try {
          // Only refresh data if we're not processing an OAuth callback
          if (!(code && state && platform)) {
            await Promise.all([refreshUserData(), refreshAccounts()]);
          }
          setInitialLoadComplete(true);
        } catch (error) {
          console.error('Error loading initial data:', error);
          setInitialLoadComplete(true); // Still mark as complete even on error
        }
      };
      
      loadInitialData();
    }
    
    return () => {
      console.log('ProfilePage - UNMOUNTED');
    };
  }, [refreshUserData, refreshAccounts, code, state, platform]);
  
  // Handle tab changes
  const handleTabChange = (newTab: string) => {
    setSearchParams({ tab: newTab });
  };
  
  // Default to profile tab if none specified
  const activeTab = tab || 'profile';
  
  const tabs = [
    { id: 'profile', label: 'Profile' },
    { id: 'posts', label: 'Posts' },
    { id: 'tiers', label: 'Tiers' },
    { id: 'notifications', label: 'Notifications' },
  ];
  
  return (
    <div className="container mx-auto mt-8 px-4">
      <h1 className="text-2xl font-bold mb-6">Profile Settings</h1>
      
      <Tabs tabs={tabs} activeTab={activeTab} onChange={handleTabChange} />
      
      <div className="mt-6">
        {activeTab === 'profile' && <ProfileSettings />}
        {activeTab === 'posts' && <PostSettings />}
        {activeTab === 'tiers' && <TierSettings />}
        {activeTab === 'notifications' && <NotificationSettings />}
      </div>
    </div>
  );
};

export default ProfilePage; 